'use client';

import React, { useContext } from 'react';
import { BanknotesIcon, BriefcaseIcon, MapPinIcon } from '@heroicons/react/16/solid';
import classNames from 'classnames';
import Link from 'next/link';
import { GateStyleContext } from './GateStyleContext';
import { resolveSetting } from '../../utils/settingsHelper';
import { getLinkClasses, getLinkStyle } from '../selectors/Link/Link';
import { getDivClasses, getDivStyle } from '../selectors/Basic/divHelpers';

export function RenderItem({ item }) {
	const { settings, variant, active, showLocation, showEmploymentForms, showSalary, buttonText, ...rest } = useContext(GateStyleContext);

	const button = {};
	Object.keys(rest).forEach((key) => {
		if (key.startsWith('button.')) {
			button[key.substring(7)] = rest[key];
		}
	});

	const iconColor = resolveSetting(button.background, settings) === 'transparent' ? resolveSetting(button.color, settings) : resolveSetting(button.background, settings);

	function Location({ children }) {
		if (!showLocation) {
			return null;
		}
		return (
			<div className="flex gap-2 truncate">
				<MapPinIcon className="h-4 w-4 flex-shrink-0" style={{ color: iconColor }} />
				<p className="truncate">
					{children}
				</p>
			</div>
		);
	}

	function Salary({ children }) {
		if (!showSalary) {
			return null;
		}
		return (
			<div className="flex gap-2 truncate">
				<BanknotesIcon className="h-4 w-4 flex-shrink-0" style={{ color: iconColor }} />
				<p className="truncate">
					{children}
				</p>
			</div>
		);
	}

	function EmploymentForms({ items }) {
		if (!items || !showEmploymentForms || items.length === 0) {
			return null;
		}
		return (
			<div className="flex gap-2 truncate">
				<BriefcaseIcon className="h-4 w-4 flex-shrink-0" style={{ color: iconColor }} />
				<p className="truncate text-xs">
					{items.map((i) => (i.locale ? i.locale[item.language] : i.name)).filter((i) => i).join(', ')}
				</p>
			</div>
		);
	}

	function JobButton({ id }) {
		return (
			<Link
				style={getLinkStyle(button, settings)}
				className={classNames(
					getLinkClasses(button, settings),
					'block',
					'text-center gap-x-2',
					'hover:opacity-75',
				)}
				// target="_blank"
				href={active ? `/jobs/${id}` : '#'}
			>
				{buttonText}
			</Link>
		);
	}

	const divClasses = getDivClasses(rest, settings);
	const interactiveClasses = active && rest.interactive ? 'transition ease-in-out delay-50 hover:-translate-y-4 duration-300' : '';
	const divStyleCol = getDivStyle({ ...rest, alignItems: 'start', display: 'block' }, settings);
	const divStyleRow = getDivStyle({ ...rest, alignItems: 'stretch', display: 'block' }, settings);

	switch (variant) {
	case 'list':
		return (
			<li
				className={classNames('truncate py-5 px-6')}
			>
				<div className="flex items-center w-full justify-between gap-x-6">
					<div className="min-w-0 overflow-hidden">
						<div className="flex items-center gap-x-3">
							<p className="text-left text-base font-bold leading-6">{item.title}</p>
						</div>
						<div className="mt-1 flex items-center gap-x-2 text-xs leading-5">
							{item.location && (<Location>{item.location}</Location>)}
							{item.salary && (<Salary>{item.salary}</Salary>)}
							<EmploymentForms items={item.employmentForms} />
						</div>
					</div>
					<div className="flex flex-none items-center gap-x-4">
						<JobButton id={item.id} />
					</div>
				</div>
			</li>
		);
	case 'list2':
		return (
			<li
				className={classNames(
					'py-5 px-6',
					divClasses,
					interactiveClasses,
				)}
				style={divStyleRow}
			>
				<div className="flex items-center w-full justify-between gap-x-6 w-full">
					<div className="min-w-0 overflow-hidden">
						<div className="flex items-center gap-x-3">
							<p className="text-left text-base font-bold leading-6">{item.title}</p>
						</div>
						<div className="mt-1 flex items-center gap-x-2 text-xs leading-5">
							{item.location && (<Location>{item.location}</Location>)}
							{item.salary && (<Salary>{item.salary}</Salary>)}
							<EmploymentForms items={item.employmentForms} />
						</div>
					</div>
					<div className="flex flex-none items-center gap-x-4">
						<JobButton id={item.id} />
					</div>
				</div>
			</li>
		);

	case 'card':
		return (
			<li
				// href={active ? `/jobs/${item.id}` : '#'}
				style={divStyleCol}
				className={classNames('relative p-6 block', divClasses, interactiveClasses)}
			>
				<div className="overflow-hidden w-full flex flex-col gap-2">
					<div className="mb-2">
						<h2 className="text-xl font-bold text-left">{item.title}</h2>
					</div>
					<div className="my-1 flex items-center overflow-hidden gap-x-2 text-xs leading-5">
						{item.location && (<Location>{item.location}</Location>)}
						{item.salary && (<Salary>{item.salary}</Salary>)}
					</div>
					<EmploymentForms items={item.employmentForms} />
					<div className="flex mt-1">
						<JobButton id={item.id} />
					</div>
				</div>
			</li>
		);
	case 'card2':
	default:
		return (
			<li
				key={item.id}
				style={divStyleCol}
				className={classNames(divClasses, interactiveClasses)}
			>
				<div className="flex w-full items-center justify-between overflow-hidden space-x-6 p-6">
					<div className="flex-1 truncate space-y-2">
						<div className="flex items-center space-x-3">
							<h3 className="truncate font-bold">{item.title}</h3>
						</div>
						<div className="mt-1 flex items-center gap-x-2 text-xs leading-5">
							{item.location && (<Location>{item.location}</Location>)}
							{item.salary && (<Salary>{item.salary}</Salary>)}
							<EmploymentForms>{item.employmentForms}</EmploymentForms>
						</div>
						<JobButton id={item.id} />
					</div>
				</div>
			</li>
		);
	}
}
